import type { AuthConfig } from '../../app/types';

export interface CognitoTokens {
  readonly idToken: string;
  readonly accessToken: string;
  readonly refreshToken?: string;
  readonly tokenType: string;
  readonly expiresAt: number;
}

export interface PkceLoginRequest {
  readonly authorizeUrl: string;
  readonly state: string;
  readonly verifier: string;
}

interface TokenResponse {
  readonly id_token: string;
  readonly access_token: string;
  readonly refresh_token?: string;
  readonly token_type: string;
  readonly expires_in: number;
}

interface InitiateAuthResponse {
  readonly AuthenticationResult?: {
    readonly IdToken: string;
    readonly AccessToken: string;
    readonly RefreshToken?: string;
    readonly ExpiresIn: number;
    readonly TokenType: string;
  };
  readonly ChallengeName?: string;
}

export async function createHostedLoginRequest(authConfig: AuthConfig): Promise<PkceLoginRequest> {
  const verifier = randomUrlSafeString(64);
  const state = randomUrlSafeString(32);
  const challenge = await pkceChallenge(verifier);
  const authorizeUrl = new URL(`${authConfig.cognitoDomain}/oauth2/authorize`);
  authorizeUrl.searchParams.set('client_id', authConfig.cognitoClientId);
  authorizeUrl.searchParams.set('response_type', 'code');
  authorizeUrl.searchParams.set('scope', 'openid email profile');
  authorizeUrl.searchParams.set('redirect_uri', authConfig.loginRedirectUri);
  authorizeUrl.searchParams.set('code_challenge_method', 'S256');
  authorizeUrl.searchParams.set('code_challenge', challenge);
  authorizeUrl.searchParams.set('state', state);

  return {
    authorizeUrl: authorizeUrl.toString(),
    state,
    verifier,
  };
}

export async function exchangeAuthorizationCode(
  authConfig: AuthConfig,
  code: string,
  verifier: string
): Promise<CognitoTokens> {
  return requestTokens(authConfig, {
    grant_type: 'authorization_code',
    client_id: authConfig.cognitoClientId,
    code,
    code_verifier: verifier,
    redirect_uri: authConfig.loginRedirectUri,
  });
}

export async function refreshHostedSession(authConfig: AuthConfig, refreshToken: string): Promise<CognitoTokens> {
  return requestTokens(authConfig, {
    grant_type: 'refresh_token',
    client_id: authConfig.cognitoClientId,
    refresh_token: refreshToken,
  }, refreshToken);
}

export async function authenticateWithPassword(
  authConfig: AuthConfig,
  username: string,
  password: string
): Promise<CognitoTokens> {
  const response = await requestUserPoolApi<InitiateAuthResponse>(
    authConfig,
    'AWSCognitoIdentityProviderService.InitiateAuth',
    {
      ClientId: authConfig.cognitoClientId,
      AuthFlow: 'USER_PASSWORD_AUTH',
      AuthParameters: {
        USERNAME: username,
        PASSWORD: password,
      },
    }
  );

  if (response.ChallengeName) {
    throw new Error(`Additional challenge required: ${response.ChallengeName}.`);
  }

  const result = response.AuthenticationResult;
  if (!result) {
    throw new Error('Authentication failed: no token payload returned.');
  }

  return {
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    refreshToken: result.RefreshToken,
    tokenType: result.TokenType || 'Bearer',
    expiresAt: Date.now() + result.ExpiresIn * 1000,
  };
}

export async function requestPasswordReset(authConfig: AuthConfig, username: string): Promise<void> {
  await requestUserPoolApi(
    authConfig,
    'AWSCognitoIdentityProviderService.ForgotPassword',
    {
      ClientId: authConfig.cognitoClientId,
      Username: username,
    }
  );
}

export async function confirmPasswordReset(
  authConfig: AuthConfig,
  username: string,
  confirmationCode: string,
  newPassword: string
): Promise<void> {
  await requestUserPoolApi(
    authConfig,
    'AWSCognitoIdentityProviderService.ConfirmForgotPassword',
    {
      ClientId: authConfig.cognitoClientId,
      Username: username,
      ConfirmationCode: confirmationCode,
      Password: newPassword,
    }
  );
}

export function hasOauthCallback(url: URL): boolean {
  return url.searchParams.has('code') && url.searchParams.has('state');
}

export function tokenExpired(tokens: CognitoTokens, skewMs = 60_000): boolean {
  return Date.now() + skewMs >= tokens.expiresAt;
}

function randomUrlSafeString(length: number) {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes).slice(0, length);
}

async function pkceChallenge(verifier: string) {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

async function requestTokens(
  authConfig: AuthConfig,
  formValues: Record<string, string>,
  refreshToken?: string
): Promise<CognitoTokens> {
  const body = new URLSearchParams(formValues);
  const response = await fetch(`${authConfig.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `Token exchange failed with status ${response.status}.`);
  }

  const payload = (await response.json()) as TokenResponse;
  return {
    idToken: payload.id_token,
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token ?? refreshToken,
    tokenType: payload.token_type,
    expiresAt: Date.now() + payload.expires_in * 1000,
  };
}

function base64UrlEncode(bytes: Uint8Array) {
  let binary = '';
  bytes.forEach((value) => {
    binary += String.fromCharCode(value);
  });

  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

async function requestUserPoolApi<T = Record<string, unknown>>(
  authConfig: AuthConfig,
  target: string,
  payload: Record<string, unknown>
): Promise<T> {
  const response = await fetch(`https://cognito-idp.${authConfig.region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': target,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorPayload = (await response.json().catch(() => ({}))) as {
      __type?: string;
      message?: string;
      Message?: string;
    };
    const errorType = (errorPayload.__type ?? '').split('#').pop() ?? '';
    const errorMessage = errorPayload.message ?? errorPayload.Message ?? `Request failed: ${response.status}`;

    if (errorType === 'NotAuthorizedException') {
      throw new Error('Invalid email or password.');
    }
    if (errorType === 'UserNotConfirmedException') {
      throw new Error('User is not confirmed.');
    }
    if (errorType === 'PasswordResetRequiredException') {
      throw new Error('Password reset required before login.');
    }
    if (errorType === 'CodeMismatchException') {
      throw new Error('Invalid password reset code.');
    }
    if (errorType === 'ExpiredCodeException') {
      throw new Error('Password reset code has expired.');
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as T;
}
